import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { map, filter, distinctUntilChanged } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';

// Store
import { selectAllProjects } from 'app/store/projects/projects.selectors';
import { selectPricingInfo } from 'app/store/pricing/pricing.selectors';
import { togglePricingModal } from 'app/store/pricing/pricing.actions';
import { loadPricingInfo } from 'app/store/pricing/pricing.actions';
import { loadProjects } from 'app/store/projects/projects.actions';

@Injectable({
    providedIn: 'root'
})
export class ProjectGuard implements CanActivate {
    constructor(
        private store: Store,
        private _router: Router,
        private _snackBar: MatSnackBar
    ) { }

    canActivate(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> {
        this.store.dispatch(loadPricingInfo());
        this.store.dispatch(loadProjects());

        return combineLatest([
            this.store.select(selectAllProjects).pipe(
                filter(projects => projects !== null && projects.length > 0),
                distinctUntilChanged()
            ),
            this.store.select(selectPricingInfo).pipe(
                filter(pricingInfo => pricingInfo !== null && pricingInfo.developerPlanId !== null),
                distinctUntilChanged()
            )
        ]).pipe(
            map(([projects, pricingInfo]) => {
                const projectId = route.params['id'];
                const projectIndex = projects.findIndex(p => p.id === projectId);

                if (projectIndex === -1) {
                    this._router.navigate(['/project']);
                    this._snackBar.open('This project doesn\'t exist in your account', 'OK', {
                        duration: 3000,
                        panelClass: ['snackbar-error']
                    });
                    return false;
                }

                const canAccess = (
                    (pricingInfo.developerPlanId === 'starter' && projectIndex === 0) ||
                    (pricingInfo.developerPlanId === 'studio' && (projectIndex === 0 || projectIndex === 1)) ||
                    pricingInfo.developerPlanId === 'pro'
                );

                if (!canAccess) {
                    this._router.navigate(['/project']);
                    this._snackBar.open('You don\'t have access to this project. Please upgrade your plan.', 'UPGRADE', {
                        duration: 3000,
                        panelClass: ['snackbar-info']
                    }).onAction().subscribe(() => {
                        this.store.dispatch(togglePricingModal({ show: true }));
                    });
                }

                return canAccess;
            })
        );
    }
}
